import { ChangeEvent, FormEvent, useState } from 'react';
import { useCreateService } from 'TenantModule/hooks/useCreateService';

export interface RegisterNewServiceFields {
  name: string;
  serviceName: string;
  cloudIntegrationName: string;
  onPremIntegrationName: string;
  serviceDisplayName: string;
  onPremIntegrationDisplayName: string;
  isIntegrationAgent: boolean;
  canBeSeeded: boolean;
  mustBeSeeded: boolean;
  seedBucket: string;
  sourceSeedBucket: string;
  requiredForSubscription: boolean;
  requiredForSandbox: boolean;
}

// Alphanumeric words separated by single dashes (e.g. "rails-api") —
// no leading/trailing/consecutive dashes. Matches what galaxy-server
// expects for DNS records and service references.
export const SERVICE_NAME_PATTERN = /^[a-zA-Z0-9]+(-[a-zA-Z0-9]+)*$/;

export const SERVICE_NAME_HELPER_TEXT =
  'Use alphanumeric words separated by single dashes, e.g. rails-api';

const defaultValues: RegisterNewServiceFields = {
  name: '',
  serviceName: '',
  cloudIntegrationName: '',
  onPremIntegrationName: '',
  serviceDisplayName: '',
  onPremIntegrationDisplayName: '',
  isIntegrationAgent: true,
  canBeSeeded: false,
  mustBeSeeded: false,
  seedBucket: '',
  sourceSeedBucket: '',
  requiredForSubscription: false,
  requiredForSandbox: false,
};

export const useRegisterNewServiceForm = () => {
  const [fields, setFields] = useState<RegisterNewServiceFields>(defaultValues);
  const { createService, isCreatingService } = useCreateService();

  const isSeedBucketRequired = fields.canBeSeeded || fields.mustBeSeeded;
  const isSourceSeedBucketRequired = fields.mustBeSeeded;

  // Only flag fields the user has typed into; `required` covers empty ones.
  const isNameInvalid = fields.name !== '' && !SERVICE_NAME_PATTERN.test(fields.name);
  const isServiceNameInvalid =
    fields.serviceName !== '' && !SERVICE_NAME_PATTERN.test(fields.serviceName);

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, type } = event.target;

    if (type === 'checkbox') {
      const checked = event.target.checked;
      if (name === 'mustBeSeeded' && checked) {
        setFields((prev) => ({ ...prev, mustBeSeeded: true, canBeSeeded: true }));
      } else if (name === 'canBeSeeded' && !checked) {
        setFields((prev) => ({ ...prev, canBeSeeded: false, mustBeSeeded: false }));
      } else {
        setFields((prev) => ({ ...prev, [name]: checked }));
      }
    } else {
      setFields((prev) => ({ ...prev, [name]: event.target.value }));
    }
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // `required` on the inputs blocks browser submits, but keep the hook's own
    // contract self-contained: the display name is what the Service Name
    // dropdowns render, so a blank one must never reach galaxy-server.
    if (
      !SERVICE_NAME_PATTERN.test(fields.name) ||
      !SERVICE_NAME_PATTERN.test(fields.serviceName) ||
      fields.serviceDisplayName.trim() === ''
    ) {
      return;
    }

    createService({
      name: fields.name,
      serviceName: fields.serviceName,
      ...(fields.cloudIntegrationName && { cloudIntegrationName: fields.cloudIntegrationName }),
      ...(fields.onPremIntegrationName && { onPremIntegrationName: fields.onPremIntegrationName }),
      serviceDisplayName: fields.serviceDisplayName.trim(),
      ...(fields.onPremIntegrationDisplayName && {
        onPremIntegrationDisplayName: fields.onPremIntegrationDisplayName,
      }),
      isIntegrationAgent: fields.isIntegrationAgent,
      canBeSeeded: fields.canBeSeeded,
      mustBeSeeded: fields.mustBeSeeded,
      ...(fields.seedBucket && { seedBucket: fields.seedBucket }),
      ...(fields.sourceSeedBucket && { sourceSeedBucket: fields.sourceSeedBucket }),
      requiredForSubscription: fields.requiredForSubscription,
      requiredForSandbox: fields.requiredForSandbox,
    });
  };

  return {
    fields,
    onChange,
    onSubmit,
    isCreatingService,
    isSeedBucketRequired,
    isSourceSeedBucketRequired,
    isNameInvalid,
    isServiceNameInvalid,
  };
};
